import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { FormItem } from '@framework/form/item/FormItem.tsx';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { VerticalSpace } from '@framework/ui/components/space/VerticalSpace.tsx';
import { Button, type ButtonProps, Collapse, message, Typography } from 'antd';
import { useMemo } from 'react';
import { BooleanConditionEditorInput } from '../../../../../../inputs/expressions-editor/conditions/BooleanConditionEditorInput.tsx';
import { DrawerEditorButtonRow } from '../../../../../../ui/drawer/DrawerEditorButtonRow.tsx';
import { StyledDrawerCollapse } from '../../../../../../ui/drawer/StyledDrawerCollapse.tsx';
import { Datatype } from '../../../../../expressions/datatype/Datatype.ts';
import { type IWidgetAdvancedOption } from '../../../../Widget.ts';
import { createDefaultAdvancedOptionValue } from '../../common/option-list/util/createDefaultOptionValue.ts';
import { getColumnInput } from '../form/getColumnInput.tsx';
import { type IAdvancedSelectColumns } from '../IAdvancedSelectColumns.ts';
import { getAdvancedSelectOptionSchema } from './getAdvancedSelectOptionSchema.ts';

const { Panel } = Collapse;

export interface IAdvancedSelectOptionValueEditDrawerProps {
  editor: RelationEditor<IWidgetAdvancedOption>;
  buttonProps?: Omit<ButtonProps, 'onClick'>;
  index: number | null;
  setIndex: (index: number | null) => void;
  columns?: IAdvancedSelectColumns[];
}

/**
 * Drawer per la modifica delle opzioni del widget AdvancedSingleSelect.
 * Gestisce la creazione di opzioni con valori multipli basati sulle colonne configurate.
 */
export function AdvancedSelectOptionValueEditDrawer(
  props: IAdvancedSelectOptionValueEditDrawerProps,
) {
  const { editor, buttonProps, index, setIndex, columns } = props;

  const dynamicSchema = useMemo(() => {
    return getAdvancedSelectOptionSchema(columns);
  }, [columns]);

  const initialValues = useMemo(
    () => editor.handle.current ?? dynamicSchema.cast({}, { assert: false }),
    [dynamicSchema, editor],
  );

  return (
    <>
      <Button
        {...buttonProps}
        onClick={() => {
          setIndex(null);
          editor.handle.open(createDefaultAdvancedOptionValue());
        }}
      >
        Opzione
      </Button>
      <Drawer
        maskClosable={false}
        title="Configurazione opzione"
        size="large"
        placement="right"
        onClose={editor.handle.cancel}
        open={
          editor.handle.isVisible && editor.handle.current?.type === 'option'
        }
        destroyOnClose
      >
        <FormHandler
          layout="vertical"
          schema={dynamicSchema}
          initialValues={initialValues}
          onSubmit={async rawValues => {
            try {
              const values = await dynamicSchema.validate(rawValues);
              const type = editor.handle.apply(values as any, index);
              if (type === 'updated') {
                message.success('Opzione modificata');
              } else {
                message.success('Opzione aggiunta');
              }
            } catch (e) {
              console.error(e);
              // biome-ignore format: <tbd>
              message.error("Si è verificato un errore nella gestione dell'opzione");
            }
          }}
        >
          {/* Campi dinamici per ogni colonna */}
          {columns && columns.length > 0 && (
            <>
              <Typography.Title level={5}>
                Valori per le colonne
              </Typography.Title>
              <Typography.Text type="secondary">
                Inserisci un valore per ciascuna colonna configurata nella
                tabella.
              </Typography.Text>
              <VerticalSpace size={12} />

              {columns.map(column => (
                <FormItem
                  key={column.id}
                  name={`values.${column.id}`}
                  label={column.label}
                  layout={{
                    hideVisualLabel:
                      column.type === Datatype.Boolean ? true : undefined,
                  }}
                >
                  {getColumnInput(column)}
                </FormItem>
              ))}
            </>
          )}

          <StyledDrawerCollapse defaultActiveKey={['view']} ghost>
            <Panel header="Regole di Visualizzazione" key="view">
              <FormItem name="visible" label="Visibilità">
                <BooleanConditionEditorInput name="visible" label="Visibile" />
              </FormItem>
              <FormItem name="enabled" label="Abilitato">
                <BooleanConditionEditorInput name="enabled" label="Abilitato" />
              </FormItem>
            </Panel>
          </StyledDrawerCollapse>

          <DrawerEditorButtonRow editor={editor} />
        </FormHandler>
      </Drawer>
    </>
  );
}
